// src/utils/distanceUtils.js
// --------------------------
// Utility functions for normalizing country names, looking up distances,
// finding nearest correct country, and calculating a percentage‐score.

const MAX_DEFAULT_DISTANCE = 8000;

/**
 * normalizeCountryName(name)
 * ─────────────────────────────
 * Convert a country name to a standard, lowercase, whitespace‐normalized string,
 * so lookups against `countryDistances.json` are consistent.
 */
export function normalizeCountryName(name) {
  if (!name) return "";
  return name.toLowerCase().trim().replace(/\s+/g, " ");
}

/**
 * buildCountryDistancesMap(countryDistances)
 * ───────────────────────────────────────────
 * Given the raw `countryDistances.json` object (keys like "France|Germany"),
 * build a Map<"france|germany", originalKey> for quick lookups.
 */
export function buildCountryDistancesMap(countryDistances) {
  const map = new Map();
  const processedSet = new Set();

  Object.keys(countryDistances).forEach((key) => {
    const [c1, c2] = key.split("|");
    if (!c1 || !c2) {
      console.warn(`Skipping invalid key: ${key}`);
      return;
    }
    const n1 = normalizeCountryName(c1);
    const n2 = normalizeCountryName(c2);
    map.set(`${n1}|${n2}`, key);
    processedSet.add(n1);
    processedSet.add(n2);
  });

  return { map, processedSet };
}

/**
 * lookupDistanceData(from, to, countryDistances)
 * ────────────────────────────────────────────────
 * Look up the distance+bearing from `from` to `to` in the nested JSON.
 * If not found both ways, returns a default high‐distance + bearing = 0.
 */
export function lookupDistanceData(fromCountryName, toCountryName, countryDistances) {
  if (!fromCountryName || !toCountryName) {
    return { distance: MAX_DEFAULT_DISTANCE, bearing: 0 };
  }
  const fromData = countryDistances[fromCountryName];
  const toData = countryDistances[toCountryName];

  // Case: direct lookup (from → to)
  if (fromData && fromData[toCountryName]) {
    return fromData[toCountryName];
  }

  // Case: reverse lookup (to → from), flip bearing
  if (toData && toData[fromCountryName]) {
    const rev = toData[fromCountryName];
    return {
      distance: rev.distance,
      bearing: (rev.bearing + 180) % 360,
    };
  }

  console.warn(`Distance data not found for ${fromCountryName} → ${toCountryName}`);
  return { distance: MAX_DEFAULT_DISTANCE, bearing: 0 };
}

/**
 * findNearestCorrectCountryInfo(guess, correctList, countryDistances)
 * ─────────────────────────────────────────────────────────────────────
 * Given a guessed country name (string) and a list of correct country names,
 * this returns { distance, bearing, nearestCorrectCountry } indicating which
 * correct country is geographically closest to the guess. If none found,
 * returns default max distance + bearing=0.
 */
export function findNearestCorrectCountryInfo(guessedCountryName, correctCountryNames, countryDistances) {
  let minDistance = Infinity;
  let nearestBearing = 0;
  let nearestCorrectCountryName = "";

  if (!guessedCountryName || !Array.isArray(correctCountryNames) || correctCountryNames.length === 0) {
    return { distance: MAX_DEFAULT_DISTANCE, bearing: 0, nearestCorrectCountry: null };
  }

  correctCountryNames.forEach((correctName) => {
    try {
      const data = lookupDistanceData(guessedCountryName, correctName, countryDistances);
      if (data.distance < minDistance) {
        minDistance = data.distance;
        nearestBearing = data.bearing;
        nearestCorrectCountryName = correctName;
      }
    } catch (err) {
      console.error(`Error looking up distance for ${guessedCountryName} to ${correctName}:`, err);
    }
  });

  if (minDistance === Infinity) {
    return {
      distance: MAX_DEFAULT_DISTANCE,
      bearing: 0,
      nearestCorrectCountry: correctCountryNames[0] || null,
    };
  }
  return {
    distance: minDistance,
    bearing: nearestBearing,
    nearestCorrectCountry: nearestCorrectCountryName,
  };
}

/**
 * calculatePercentage(distance)
 * ───────────────────────────────
 * Convert a distance (in km) to a percentage score. 0 km => 100%, large distances => 0%.
 */
export function calculatePercentage(distance) {
  if (distance == null) return 0;
  if (distance === 0) return 100;
  return Math.max(0, 100 - Math.round((distance / 20000) * 100));
}
